import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Image as ImageIcon, Play, Star, TrendingUp } from 'lucide-react';
import {
  creativeWorkbenchApi,
  adCatalogApi,
  type WorkbenchAssetSearchItem,
  type AdCatalogCreativeSummary,
  type AdCatalogCreativeReviewSummary,
} from '@/services/api';
import type { AssetFilters } from './AssetFilterRail';

interface Props {
  clientUid: string;
  filters: AssetFilters;
  mode: 'assets' | 'creatives';
  onAssetClick: (id: string) => void;
  onCreativeClick: (id: string) => void;
}

/** Returns true if the item has a browser-renderable preview image. */
function hasVisualPreview(item: WorkbenchAssetSearchItem): boolean {
  const src = item.preview_url ?? item.thumbnail_url;
  return !!src && /^https?:/i.test(src);
}

export function AssetGallery({ clientUid, filters, mode, onAssetClick, onCreativeClick }: Props) {
  // --- workbench assets state ---
  const [assetItems, setAssetItems] = useState<WorkbenchAssetSearchItem[]>([]);
  const [assetTotal, setAssetTotal] = useState(0);
  const [assetLoading, setAssetLoading] = useState(false);

  // --- ad-catalog creatives state ---
  const [catalogCreatives, setCatalogCreatives] = useState<AdCatalogCreativeSummary[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Load workbench assets (for "All Assets" tab)
  useEffect(() => {
    if (mode !== 'assets') return;
    let cancelled = false;
    setAssetLoading(true);
    creativeWorkbenchApi
      .searchAssets({
        clientUid,
        q: filters.query || undefined,
        assetType: filters.assetType ?? undefined,
        sceneTag: filters.sceneTag ?? undefined,
        channel: filters.channel ?? undefined,
        playbookId: filters.playbookId ?? undefined,
        briefId: filters.briefId ?? undefined,
        brandCompliant: filters.brandCompliant ?? undefined,
        limit: 60,
      })
      .then((r) => {
        if (cancelled) return;
        const filtered = r.items.filter((item) => item.quality_score >= filters.quality);
        setAssetItems(filtered);
        setAssetTotal(r.total_estimate);
      })
      .catch(() => {
        if (!cancelled) setAssetItems([]);
      })
      .finally(() => {
        if (!cancelled) setAssetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, filters, mode]);

  // Load ad-catalog creatives (for "Creatives" tab)
  useEffect(() => {
    if (mode !== 'creatives') return;
    let cancelled = false;
    setCatalogLoading(true);
    // Map assetType filter chip: "video" → media_type=video, others ignored for creatives
    const mediaTypeFilter = filters.assetType === 'video' ? 'video' : undefined;
    adCatalogApi
      .listCreatives({
        client_uid: clientUid,
        q: filters.query || undefined,
        media_type: mediaTypeFilter,
        limit: 60,
      })
      .then((r) => {
        if (cancelled) return;
        setCatalogCreatives(r.items);
      })
      .catch(() => {
        if (!cancelled) setCatalogCreatives([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, filters, mode]);

  const loading = mode === 'assets' ? assetLoading : catalogLoading;
  const isEmpty =
    mode === 'assets' ? !assetItems.length : !catalogCreatives.length;

  if (loading && isEmpty) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="bg-slate-100 rounded-xl animate-pulse"
            style={{ height: 220 }}
          />
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="border border-dashed border-slate-200 rounded-xl py-16 text-center text-xs text-slate-400">
        {mode === 'creatives'
          ? 'No ingested creatives found for this client.'
          : 'No assets match these filters.'}
      </div>
    );
  }

  if (mode === 'creatives') {
    return (
      <CatalogCreativesGrid
        items={catalogCreatives}
        filters={filters}
        onCreativeClick={onCreativeClick}
      />
    );
  }

  return (
    <AssetsGrid
      items={assetItems}
      total={assetTotal}
      onAssetClick={onAssetClick}
    />
  );
}

// ─── Ad-catalog Creatives grid ─────────────────────────────────────────────

function hasRealMedia(creative: AdCatalogCreativeSummary): boolean {
  const { thumbnail_url, video_url, media_type } = creative.form;
  return (
    (!!thumbnail_url && /^https?:/i.test(thumbnail_url)) ||
    (!!video_url && /^https?:/i.test(video_url)) ||
    media_type === 'video'
  );
}

/** Strip unresolved Facebook DPA template variables like {{product.name}} */
function safeHeadline(headline: string | null | undefined): string | null {
  if (!headline) return null;
  if (/\{\{/.test(headline)) return null;
  return headline;
}

function sortCreatives(
  items: AdCatalogCreativeSummary[],
  sortBy: 'spend' | 'ctr' | 'score' | 'recent',
): AdCatalogCreativeSummary[] {
  return [...items].sort((a, b) => {
    if (sortBy === 'ctr') {
      return (b.performance.ctr ?? 0) - (a.performance.ctr ?? 0);
    }
    if (sortBy === 'score') {
      const scoreA = Math.max(
        0,
        ...Object.values(a.meaning.creative_reviews ?? {}).map(
          (r) => r.overall_score ?? 0,
        ),
      );
      const scoreB = Math.max(
        0,
        ...Object.values(b.meaning.creative_reviews ?? {}).map(
          (r) => r.overall_score ?? 0,
        ),
      );
      return scoreB - scoreA;
    }
    // Default: spend desc (also handles 'recent' as spend fallback)
    return (b.performance.spend ?? 0) - (a.performance.spend ?? 0);
  });
}

function CatalogCreativesGrid({
  items,
  filters,
  onCreativeClick,
}: {
  items: AdCatalogCreativeSummary[];
  filters: AssetFilters;
  onCreativeClick: (id: string) => void;
}) {
  // Apply activeOnly filter: creatives with any spend are "active"
  const filtered = filters.activeOnly
    ? items.filter((c) => (c.performance.spend ?? 0) > 0)
    : items;

  const withMedia = filtered.filter(hasRealMedia);
  const hiddenCount = filtered.length - withMedia.length;

  const sorted = sortCreatives(withMedia, filters.sortBy ?? 'spend');

  return (
    <div className="space-y-4">
      <div className="text-[10px] font-mono text-slate-400">
        {sorted.length} creative{sorted.length !== 1 ? 's' : ''}
        {hiddenCount > 0 && (
          <span className="ml-2 opacity-50">(+{hiddenCount} without media)</span>
        )}
        {filters.activeOnly && (
          <span className="ml-2 text-teal-500">· active only</span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sorted.map((creative) => (
          <CatalogCreativeCard
            key={creative.creative_id}
            creative={creative}
            onClick={() => onCreativeClick(creative.creative_id)}
          />
        ))}
      </div>
    </div>
  );
}

function verdictColors(verdict?: string | null) {
  if (verdict === 'ready') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (verdict === 'needs_work') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (verdict === 'critical') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-50 text-slate-500 border-slate-200';
}

function topReviewSummary(
  reviews: Record<string, AdCatalogCreativeReviewSummary> | undefined,
): AdCatalogCreativeReviewSummary | null {
  if (!reviews) return null;
  const entries = Object.values(reviews);
  if (!entries.length) return null;
  // Pick highest score, fall back to most recently reviewed
  const sorted = [...entries].sort((a, b) => {
    if ((b.overall_score ?? 0) !== (a.overall_score ?? 0)) {
      return (b.overall_score ?? 0) - (a.overall_score ?? 0);
    }
    return (b.reviewed_at ?? '') > (a.reviewed_at ?? '') ? 1 : -1;
  });
  return sorted[0];
}

function CatalogCreativeCard({
  creative,
  onClick,
}: {
  creative: AdCatalogCreativeSummary;
  onClick: () => void;
}) {
  const review = topReviewSummary(creative.meaning.creative_reviews);
  const thumbnail = creative.form.thumbnail_url;
  const hasThumb = !!thumbnail && /^https?:/i.test(thumbnail);
  const isVideo = creative.form.media_type === 'video';
  const perf = creative.performance;
  const reviewCount = Object.keys(creative.meaning.creative_reviews ?? {}).length;

  return (
    <button
      onClick={onClick}
      className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-teal-300 hover:shadow-lg transition-all text-left flex flex-col"
    >
      {/* ── Thumbnail ── */}
      <div className="relative h-40 bg-slate-50 flex-shrink-0 overflow-hidden">
        {hasThumb ? (
          <img
            src={thumbnail!}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-2">
            <ImageIcon size={24} />
            <span className="text-[10px] font-mono">
              {creative.form.media_type}
            </span>
          </div>
        )}

        {/* Video play overlay */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play size={14} className="text-slate-800 fill-slate-800 ml-0.5" />
            </div>
          </div>
        )}

        {/* Verdict badge — top left */}
        {review?.verdict && (
          <span
            className={`absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${verdictColors(review.verdict)}`}
          >
            {review.verdict.replace(/_/g, ' ')}
          </span>
        )}

        {/* Score badge — top right */}
        {review?.overall_score != null && (
          <span className="absolute top-2 right-2 flex items-center gap-0.5 bg-white/90 backdrop-blur-sm text-navy-900 text-[11px] font-bold px-2 py-0.5 rounded border border-slate-200 shadow-sm">
            <Star size={9} className="text-amber-400 fill-amber-400" />
            {review.overall_score}
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        {/* Ad name */}
        <div>
          <div className="text-[12px] font-semibold text-navy-900 leading-tight line-clamp-2">
            {safeHeadline(creative.form.headline) || creative.ad_name || creative.creative_id}
          </div>
          {creative.meaning.hook_type && (
            <div className="text-[10px] font-mono text-slate-400 mt-0.5 truncate">
              {creative.meaning.hook_type}
              {creative.meaning.angle_archetype && ` · ${creative.meaning.angle_archetype}`}
            </div>
          )}
        </div>

        {/* Review summary snippets */}
        {review && (
          <div className="space-y-1">
            {review.top_strength && (
              <p className="text-[11px] text-emerald-700 line-clamp-1">
                <span className="font-mono text-[9px] text-emerald-400 uppercase mr-1">strength</span>
                {review.top_strength}
              </p>
            )}
            {review.critical_gap && (
              <p className="text-[11px] text-amber-700 line-clamp-1">
                <span className="font-mono text-[9px] text-amber-400 uppercase mr-1">gap</span>
                {review.critical_gap}
              </p>
            )}
          </div>
        )}

        {/* Performance row */}
        <div className="flex items-center gap-3 mt-auto pt-1 border-t border-slate-100">
          {perf.spend != null && perf.spend > 0 ? (
            <>
              <div className="text-[11px] font-mono text-navy-900">
                ${Number(perf.spend).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              {perf.ctr != null && perf.ctr > 0 && (
                <div className="flex items-center gap-0.5 text-[11px] font-mono text-teal-600">
                  <TrendingUp size={9} />
                  {(Number(perf.ctr) * 100).toFixed(2)}%
                </div>
              )}
            </>
          ) : (
            <span className="text-[10px] font-mono text-slate-300">no spend data</span>
          )}
          {reviewCount > 0 && (
            <span className="ml-auto text-[10px] font-mono text-slate-400">
              {reviewCount} review{reviewCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Assets grid ──────────────────────────────────────────────────────────────

function AssetsGrid({
  items,
  total,
  onAssetClick,
}: {
  items: WorkbenchAssetSearchItem[];
  total: number;
  onAssetClick: (id: string) => void;
}) {
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const visible = items.filter(hasVisualPreview);
  const hidden = items.filter((i) => !hasVisualPreview(i));

  return (
    <div className="space-y-4">
      <div className="text-[10px] font-mono text-slate-400">
        {visible.length} of {total} assets
        {hidden.length > 0 && (
          <span className="ml-1 text-slate-300">· {hidden.length} without preview</span>
        )}
      </div>

      {visible.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {visible.map((item) => (
            <AssetTile key={item.asset_id} item={item} onClick={() => onAssetClick(item.asset_id)} />
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-slate-200 rounded-xl py-12 text-center text-xs text-slate-400">
          No previewable assets found.
        </div>
      )}

      {/* ── Hidden assets section ── */}
      {hidden.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setHiddenOpen((p) => !p)}
            className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
          >
            {hiddenOpen ? (
              <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
            )}
            <span className="text-xs font-medium text-slate-500">
              Assets without preview images
            </span>
            <span className="ml-auto text-[10px] font-mono bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">
              {hidden.length}
            </span>
          </button>
          {hiddenOpen && (
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {hidden.map((item) => (
                <AssetTile
                  key={item.asset_id}
                  item={item}
                  onClick={() => onAssetClick(item.asset_id)}
                  noPreview
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Asset tile ───────────────────────────────────────────────────────────────

function AssetTile({
  item,
  onClick,
  noPreview = false,
}: {
  item: WorkbenchAssetSearchItem;
  onClick: () => void;
  noPreview?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-teal-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-teal-400/40 text-left"
    >
      {noPreview ? (
        <NoPreviewPlaceholder item={item} />
      ) : (
        <AssetPreview item={item} />
      )}
      <div className="px-2.5 py-2">
        <div className="text-[11px] font-bold text-navy-900 truncate">{item.asset_type}</div>
        <div className="text-[9px] font-mono text-slate-400 flex justify-between">
          <span>{item.channel ?? 'unknown'}</span>
          <span className="text-slate-300">{Math.round(item.quality_score * 100)}%</span>
        </div>
      </div>
    </button>
  );
}

function AssetPreview({ item }: { item: WorkbenchAssetSearchItem }) {
  const src = item.preview_url ?? item.thumbnail_url;
  if (!src || !/^https?:/i.test(src)) {
    return (
      <div className="h-32 flex items-center justify-center bg-slate-50 text-slate-300">
        <ImageIcon size={24} />
      </div>
    );
  }
  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      src={src}
      loading="lazy"
      className="h-32 w-full object-cover bg-slate-50"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

function NoPreviewPlaceholder({ item }: { item: WorkbenchAssetSearchItem }) {
  return (
    <div className="h-20 flex flex-col items-center justify-center bg-slate-50 text-slate-300 gap-1">
      <ImageIcon size={18} />
      <span className="text-[9px] font-mono text-slate-300 truncate max-w-[90%]">
        {item.asset_type}
      </span>
    </div>
  );
}
